import {useEffect, useMemo} from 'react';
import {FormSubmissionProvider, useFormSubmission} from '../contexts/form-submission-context';
import {FormHeader, FormNavigation, FormProgress, FormSubmissionCanvas, FormSuccessMessage, QuizResultsDisplay,} from '../components/form-submission';
import {Alert, AlertDescription, AlertTitle, cn, useLanguage, LoadingSkeleton} from '@shondalai/ui-commons';
import {AlertCircle} from 'lucide-react';
import {useGoogleAnalyticsIntegration} from '../hooks/use_google_analytics_integration';
import {TranslationProvider, FormLanguageSwitcher} from '../components/translations';

function FormSubmissionContent() {
  const { t } = useLanguage();

  // Get formId from Joomla options (when used as standalone view)
  const joomlaOptions = (window as any).Joomla?.getOptions?.('com_easyforms') || {};

  // Get formId
  const formId = joomlaOptions?.formId || null;

  const {
    form,
    isLoading,
    loadError,
    submissionComplete,
    submissionResponse,
    loadForm,
  } = useFormSubmission();

  // Google Analytics integration (no params needed - uses form from context)
  const { trackFormSubmission } = useGoogleAnalyticsIntegration();

  // Load form
  useEffect(() => {
    if (formId) {
      loadForm(formId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formId]);

  // Load base theme CSS when form has theme
  useEffect(() => {
    if (!form?.theme) return;

    // Load base theme CSS first (if not already loaded)
    const baseStyleId = 'theme-base';
    let baseStyleEl = document.getElementById(baseStyleId) as HTMLLinkElement;

    if (!baseStyleEl) {
      baseStyleEl = document.createElement('link');
      baseStyleEl.id = baseStyleId;
      baseStyleEl.rel = 'stylesheet';
      baseStyleEl.href = '/media/com_easyforms/themes/base.css';
      document.head.appendChild(baseStyleEl);
    }
  }, [form?.theme]);

  // Compute theme CSS variables as inline styles
  const themeStyles = useMemo(() => {
    if (!form?.theme?.theme) return undefined;

    const { theme, customizations } = form.theme;
    const variables = { ...theme.variables, ...customizations };

    // Convert theme variables to CSS custom properties
    const cssVars: Record<string, string> = {};
    Object.entries(variables).forEach(([key, value]) => {
      cssVars[`--ef-${key}`] = String(value);
    });

    return cssVars as React.CSSProperties;
  }, [form?.theme]);

  // Track form submission
  useEffect(() => {
    if (submissionComplete && submissionResponse) {
      trackFormSubmission(submissionResponse.submissionId);
    }
  }, [submissionComplete, submissionResponse, trackFormSubmission]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 py-8 px-4">
        <div className="container mx-auto max-w-4xl">
          <LoadingSkeleton variant="list" lines={8} showAvatar={false} className="mb-4" />
        </div>
      </div>
    );
  }

  // Error state
  if (loadError) {
    // Check if this is a token-related error
    const isTokenError = loadError.toLowerCase().includes('token') ||
                         loadError.toLowerCase().includes('access');

    if (isTokenError) {
      // Beautiful token error page (Swiss spa design)
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
          <div className="max-w-lg w-full">
            <div className="bg-card border border-border/50 rounded-lg shadow-sm overflow-hidden">
              {/* Header */}
              <div className="bg-muted/30 border-b border-border/50 px-8 py-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-muted-foreground" strokeWidth={1.5} />
                </div>
                <h1 className="text-2xl font-light text-foreground tracking-tight">
                  {t('COM_EASYFORMS_ACCESS_DENIED', 'Access Denied')}
                </h1>
              </div>

              {/* Content */}
              <div className="px-8 py-8 space-y-6">
                <div className="space-y-3">
                  <h2 className="text-base font-medium text-foreground">
                    {t('COM_EASYFORMS_INVALID_TOKEN_TITLE', 'Invalid or Expired Access Link')}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {t('COM_EASYFORMS_INVALID_TOKEN_MESSAGE',
                      'The access link you used is either invalid, has expired, or has already been used. This form requires a valid access token to continue.')}
                  </p>
                </div>

                {/* Divider */}
                <div className="border-t border-border/50" />

                {/* Possible reasons */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-foreground">
                    {t('COM_EASYFORMS_POSSIBLE_REASONS', 'Possible Reasons')}
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground/40 mt-0.5">•</span>
                      <span>{t('COM_EASYFORMS_TOKEN_REASON_EXPIRED', 'The link has expired')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground/40 mt-0.5">•</span>
                      <span>{t('COM_EASYFORMS_TOKEN_REASON_USED', 'The link has already been used (single-use token)')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground/40 mt-0.5">•</span>
                      <span>{t('COM_EASYFORMS_TOKEN_REASON_INVALID', 'The link is incomplete or incorrect')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-muted-foreground/40 mt-0.5">•</span>
                      <span>{t('COM_EASYFORMS_TOKEN_REASON_REVOKED', 'The access has been revoked')}</span>
                    </li>
                  </ul>
                </div>

                {/* Divider */}
                <div className="border-t border-border/50" />

                {/* Next steps */}
                <div className="space-y-3">
                  <h3 className="text-sm font-medium text-foreground">
                    {t('COM_EASYFORMS_WHAT_TO_DO', 'What You Can Do')}
                  </h3>
                  <div className="space-y-3 text-sm text-muted-foreground">
                    <p>
                      {t('COM_EASYFORMS_TOKEN_CONTACT_SENDER',
                        'Please contact the person who sent you this link to request a new access link.')}
                    </p>
                    <p className="text-xs">
                      {t('COM_EASYFORMS_TOKEN_CHECK_EMAIL',
                        'Make sure you\'re using the complete link from your invitation email and haven\'t modified it.')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="bg-muted/20 border-t border-border/50 px-8 py-4 text-center">
                <button
                  onClick={() => window.history.back()}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors font-light"
                >
                  {t('COM_EASYFORMS_GO_BACK', '← Go Back')}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Generic error for non-token errors
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
        <div className="max-w-md w-full">
          <Alert variant="destructive" className="border-destructive/20 bg-destructive/5">
            <AlertCircle className="h-4 w-4" strokeWidth={1.5} />
            <AlertTitle className="text-base font-medium">{t('COM_EASYFORMS_FORM_LOAD_ERROR_TITLE', 'Unable to Load Form')}</AlertTitle>
            <AlertDescription className="mt-1.5 text-sm">
              {loadError}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // No form found
  if (!form) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
        <div className="max-w-md w-full">
          <Alert className="border-border/40 bg-muted/30">
            <AlertCircle className="h-4 w-4" strokeWidth={1.5} />
            <AlertTitle className="text-base font-medium">{t('COM_EASYFORMS_FORM_NOT_FOUND_TITLE', 'Form Not Found')}</AlertTitle>
            <AlertDescription className="mt-1.5 text-sm">
              {t('COM_EASYFORMS_FORM_NOT_FOUND_MESSAGE', "The form you're looking for doesn't exist or is no longer available.")}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Check if user has already submitted (and multiple submissions not allowed)
  if (form.alreadySubmitted) {
    return (
      <div className="min-h-screen flex bg-gradient-to-br from-background via-background to-muted/20 p-4">
        <div className="w-full">
          <Alert className="border-amber-200/50 dark:border-amber-800/30 bg-amber-50/50 dark:bg-amber-950/30">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
            <AlertTitle className="text-base font-medium text-amber-900 dark:text-amber-100">
              {t('COM_EASYFORMS_FORM_ALREADY_SUBMITTED_TITLE', 'Already Submitted')}
            </AlertTitle>
            <AlertDescription className="mt-2 text-sm text-amber-800 dark:text-amber-200">
              <div className="space-y-3">
                <p className="font-medium">
                  {form.message || t('COM_EASYFORMS_FORM_ALREADY_SUBMITTED_MESSAGE', 'You have already submitted this form and multiple submissions are not allowed.')}
                </p>
                {form.title && (
                  <div className="pt-3 border-t border-amber-200/50 dark:border-amber-800/30">
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                      <strong>{t('COM_EASYFORMS_FORM_LABEL', 'Form')}:</strong> {form.title}
                    </p>
                    {form.description && (
                      <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                        {form.description}
                      </p>
                    )}
                  </div>
                )}
                <p className="text-xs text-amber-700 dark:text-amber-300 pt-2">
                  {t('COM_EASYFORMS_FORM_ERROR_CONTACT_ADMIN', 'If you believe this is an error, please contact the form administrator.')}
                </p>
              </div>
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Submission complete - show success message
  if (submissionComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 py-8 px-4">
        <div className="container mx-auto">
          <FormSuccessMessage />

          {/* Show quiz results if applicable */}
          {submissionResponse?.showResults && form.formType === 'quiz' && (
            <div className="mt-6">
              <QuizResultsDisplay />
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main form rendering
  return (
    <div
      className={cn(
        "easyforms-submission-form min-h-screen",
        !form?.theme && "bg-gradient-to-br from-background via-background to-muted/20"
      )}
      style={themeStyles}
    >
      <div className={cn(
        "container mx-auto py-4 px-4 md:py-5 lg:py-6"
      )}>
        {/* Language Switcher - Show if form has multilingual enabled */}
        {form?.settings?.enableMultilingual && (
          <div className="flex justify-end mb-4">
            <FormLanguageSwitcher variant="minimal" />
          </div>
        )}

        {/* Form Card */}
        <div
          className={cn(
            "easyforms-form-container",
            // Default classes (can be overridden)
            !form.settings.containerCssClass && !form?.theme && [
              "bg-card/95 backdrop-blur-sm rounded-lg border border-border/40",
              "shadow-[0_1px_3px_rgba(0,0,0,0.04),0_8px_16px_rgba(0,0,0,0.06)]",
              "p-5 md:p-8 lg:p-10",
              "transition-shadow duration-300"
            ],
            // Custom classes override defaults
            form.settings.containerCssClass
          )}
        >
          {/* Form Header */}
          <FormHeader />

          {/* Progress Indicator (for multi-page forms) */}
          <FormProgress />

          {/* Form Fields Canvas */}
          <FormSubmissionCanvas />

          {/* Navigation Buttons */}
          <FormNavigation />
        </div>

        {/* Footer Text */}
        {joomlaOptions.showCredits && (
          <div className="text-center mt-5">
            <p className="text-xs text-muted-foreground/60 font-light">
              {t('COM_EASYFORMS_FORM_POWERED_BY', 'Powered by EasyForms')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default function FormSubmission() {
  // Get formId from Joomla options
  const joomlaOptions = (window as any).Joomla?.getOptions?.('com_easyforms') || {};
  const formId = joomlaOptions?.formId || null;
  const defaultLanguage = joomlaOptions?.defaultLanguage || 'en-GB';

  return (
    <FormSubmissionProvider>
      {formId ? (
        <TranslationProvider
          formId={formId}
          defaultLanguage={defaultLanguage}
          detectionMethod="joomla"
          enableLanguageSwitcher={true}
        >
          <FormSubmissionContent />
        </TranslationProvider>
      ) : (
        <FormSubmissionContent />
      )}
    </FormSubmissionProvider>
  );
}
